import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Paper from 'material-ui/Paper'
import RaisedButton from 'material-ui/RaisedButton'
import TextField from 'material-ui/TextField'
import SelectField from 'material-ui/SelectField'
import MenuItem from 'material-ui/MenuItem'
import Checkbox from 'material-ui/Checkbox'
import CircularProgress from 'material-ui/CircularProgress'
import AutoComplete from 'material-ui/AutoComplete'
import { setSnackbarMessage } from '../../../actions/snackbar'
import api from '../../../api'

class IntakeAnswersEditor extends Component<any, any> {
  constructor (props) {
    super(props)
    this.state = {
      loading: true,
      saving: false,
      expanded: false,
      questions: [],
      answers: {},
      originalAnswers: {},
      titleOptions: [],
      institutions: [],
      institutionSearchText: {}
    }
  }

  componentDidMount () {
    this.loadIntakeAnswers()
    this.loadTitleOptions()
  }

  loadTitleOptions = async () => {
    try {
      const response = await api.get('admin/settings/titles')
      if (response.ok) {
        const data = await response.json()
        this.setState({ titleOptions: data?.value || [] })
      }
    } catch (error) {
      console.error('Error loading title options:', error)
    }
  }

  handleInstitutionSearch = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      this.setState({ institutions: [] })
      return
    }

    try {
      const response = await api.get('admin/institutions', {
        body: {
          filters: { name_ilike: searchText },
          limit: 100
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.setState({ institutions: data || [] })
      }
    } catch (error) {
      console.error('Error searching institutions:', error)
    }
  }

  handleInstitutionSelect = (questionId, chosenRequest) => {
    if (chosenRequest && chosenRequest.name) {
      this.handleAnswerChange(questionId, chosenRequest.name)
    }
  }

  loadIntakeAnswers = async () => {
    try {
      const { sessionReservationId } = this.props
      const response = await api.get(`admin/session_reservations/${sessionReservationId}/intake_answers`)

      if (!response.ok) {
        throw new Error('Failed to load intake answers')
      }

      const data = await response.json()

      // Build answers object from questions with answer_value
      const answers = {}
      const originalAnswers = {}

      if (data.questions && Array.isArray(data.questions)) {
        data.questions.forEach(q => {
          const value = q.answer_value !== null ? String(q.answer_value) : ''
          answers[q.id] = value
          originalAnswers[q.id] = value
        })
      }

      this.setState({
        questions: data.questions || [],
        answers,
        originalAnswers,
        loading: false
      })
    } catch (error) {
      console.error('Error loading intake answers:', error)
      this.props.setSnackbarMessage('Error loading intake answers')
      this.setState({ loading: false })
    }
  }

  handleToggleExpand = () => {
    this.setState(prevState => ({ expanded: !prevState.expanded }))
  }

  handleAnswerChange = (questionId, value) => {
    this.setState(prevState => ({
      answers: {
        ...prevState.answers,
        [questionId]: value
      }
    }))
  }

  handleCheckboxChange = (questionId, optionValue, isChecked) => {
    this.setState(prevState => {
      const currentValue = prevState.answers[questionId]
      let currentArray = []

      // Parse current value if it's a string
      if (typeof currentValue === 'string' && currentValue) {
        try {
          currentArray = JSON.parse(currentValue)
        } catch {
          currentArray = []
        }
      } else if (Array.isArray(currentValue)) {
        currentArray = [...currentValue]
      }

      // Add or remove the option
      if (isChecked) {
        if (!currentArray.includes(optionValue)) {
          currentArray.push(optionValue)
        }
      } else {
        currentArray = currentArray.filter(v => v !== optionValue)
      }

      return {
        answers: {
          ...prevState.answers,
          [questionId]: currentArray
        }
      }
    })
  }

  handleSave = async () => {
    try {
      this.setState({ saving: true })
      const { sessionReservationId } = this.props
      const { questions, answers } = this.state

      // Build the answers array for the API
      const answersArray = questions
        .filter(q => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== '')
        .map(q => ({
          intake_question_id: q.id,
          answer_value: answers[q.id]
        }))

      const response = await api.put(`admin/session_reservations/${sessionReservationId}/intake_answers`, {
        body: { answers: answersArray }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to save intake answers')
      }

      this.props.setSnackbarMessage('Intake answers saved successfully')

      // Reload to get fresh data
      await this.loadIntakeAnswers()
    } catch (error) {
      console.error('Error saving intake answers:', error)
      this.props.setSnackbarMessage(error.message || 'Error saving intake answers')
    } finally {
      this.setState({ saving: false })
    }
  }

  renderQuestionField = (question) => {
    const { answers, titleOptions, institutions } = this.state
    const currentValue = answers[question.id] !== undefined ? answers[question.id] : ''

    // Handle special case for university/institution selection
    if (question.storage_path === 'university' && question.storage_table === 'users') {
      return (
        <AutoComplete
          floatingLabelText={question.question_text + (question.is_required ? ' *' : '')}
          searchText={currentValue}
          onUpdateInput={(searchText) => {
            this.handleAnswerChange(question.id, searchText)
            this.handleInstitutionSearch(searchText)
          }}
          onNewRequest={(chosenRequest) => this.handleInstitutionSelect(question.id, chosenRequest)}
          dataSource={institutions}
          dataSourceConfig={{ text: 'name', value: 'name' }}
          filter={AutoComplete.noFilter}
          openOnFocus
          fullWidth
        />
      )
    }

    // Handle special case for title selection
    if (question.storage_path === 'title' && question.storage_table === 'users') {
      return (
        <SelectField
          floatingLabelText={question.question_text + (question.is_required ? ' *' : '')}
          value={currentValue}
          onChange={(event, index, value) => this.handleAnswerChange(question.id, value)}
          fullWidth
        >
          <MenuItem value='' />
          {titleOptions.map((option, idx) => (
            <MenuItem key={idx} value={option.value} primaryText={option.name} />
          ))}
        </SelectField>
      )
    }

    switch (question.question_type) {
      case 'text':
        return (
          <TextField
            floatingLabelText={question.question_text + (question.is_required ? ' *' : '')}
            value={currentValue}
            onChange={(event) => this.handleAnswerChange(question.id, event.target.value)}
            fullWidth
            multiLine
            rows={2}
          />
        )

      case 'number':
        return (
          <TextField
            floatingLabelText={question.question_text + (question.is_required ? ' *' : '')}
            value={currentValue}
            onChange={(event) => this.handleAnswerChange(question.id, event.target.value)}
            type='number'
            fullWidth
          />
        )

      case 'select': {
        const options = question.options || []
        return (
          <SelectField
            floatingLabelText={question.question_text + (question.is_required ? ' *' : '')}
            value={currentValue}
            onChange={(event, index, value) => this.handleAnswerChange(question.id, value)}
            fullWidth
          >
            <MenuItem value='' />
            {options.map((option, idx) => {
              const optionValue = typeof option === 'object' ? option.value : option
              const optionName = typeof option === 'object' ? option.name : option
              return (
                <MenuItem key={idx} value={optionValue} primaryText={optionName} />
              )
            })}
          </SelectField>
        )
      }

      case 'checkbox': {
        const options = question.options || []
        let selectedValues = []

        // Parse current value
        if (typeof currentValue === 'string' && currentValue) {
          try {
            selectedValues = JSON.parse(currentValue)
          } catch {
            selectedValues = []
          }
        } else if (Array.isArray(currentValue)) {
          selectedValues = currentValue
        }

        return (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 12, color: 'rgba(0, 0, 0, 0.3)' }}>
              {question.question_text + (question.is_required ? ' *' : '')}
            </div>
            {options.map((option, idx) => {
              const optionValue = typeof option === 'object' ? option.value : option
              const optionName = typeof option === 'object' ? option.name : option
              const isChecked = selectedValues.includes(optionValue)

              return (
                <Checkbox
                  key={idx}
                  label={optionName}
                  checked={isChecked}
                  onCheck={(event, checked) => this.handleCheckboxChange(question.id, optionValue, checked)}
                  style={{ marginBottom: 8 }}
                />
              )
            })}
          </div>
        )
      }

      default:
        return <div>Unsupported question type: {question.question_type}</div>
    }
  }

  render () {
    const { loading, saving, expanded, questions } = this.state

    if (loading) {
      return (
        <Paper style={{ padding: 16, marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <CircularProgress size={20} />
            <span>Loading intake answers...</span>
          </div>
        </Paper>
      )
    }

    if (questions.length === 0) {
      return null // Don't show anything if there are no questions
    }

    // Separate questions by storage table
    const sessionQuestions = questions.filter(q => q.storage_table !== 'users')
    const userQuestions = questions.filter(q => q.storage_table === 'users')

    return (
      <Paper style={{ padding: 16, marginTop: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={this.handleToggleExpand}
        >
          <h3 style={{ margin: 0 }}>
            Intake Question Answers ({questions.length})
          </h3>
          <span style={{ fontSize: 20, fontWeight: 'bold' }}>
            {expanded ? '−' : '+'}
          </span>
        </div>

        {expanded && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e0e0e0' }}>
            {/* Session-specific questions */}
            {sessionQuestions.length > 0 && (
              <div>
                <h4 style={{ marginTop: 0, marginBottom: 16, color: '#333' }}>
                  Session-Specific Information
                </h4>
                {sessionQuestions.map((question) => (
                  <div key={question.id} style={{ marginBottom: 24 }}>
                    {this.renderQuestionField(question)}
                  </div>
                ))}
              </div>
            )}

            {/* User-level questions */}
            {userQuestions.length > 0 && (
              <div style={{ marginTop: sessionQuestions.length > 0 ? 32 : 0 }}>
                <h4 style={{ marginTop: 0, marginBottom: 8, color: '#333' }}>
                  User Profile Information
                </h4>
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: 4,
                  padding: 12,
                  marginBottom: 16,
                  fontSize: 13
                }}>
                  <strong>Note:</strong> This information is stored at the user level and will affect the user's profile across all sessions.
                </div>
                {userQuestions.map((question) => (
                  <div key={question.id} style={{ marginBottom: 24 }}>
                    {this.renderQuestionField(question)}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
              <RaisedButton
                label={saving ? 'Saving...' : 'Save Intake Answers'}
                primary
                onClick={this.handleSave}
                disabled={saving}
                icon={saving ? <CircularProgress size={20} thickness={2} /> : null}
              />
            </div>
          </div>
        )}
      </Paper>
    )
  }
}

// @ts-ignore
IntakeAnswersEditor.propTypes = {
  sessionReservationId: PropTypes.number.isRequired,
  setSnackbarMessage: PropTypes.func.isRequired
}

const mapDispatchToProps = dispatch => bindActionCreators({ setSnackbarMessage }, dispatch)

// @ts-ignore
export default connect(null, mapDispatchToProps)(IntakeAnswersEditor)
